import React, { useEffect, useRef, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";

interface VideoThumbnailProps {
    videoUrl?: string | null;
    className?: string;
    alt?: string;
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
    videoUrl,
    className,
    alt,
}) => {
    const isMobile = useIsMobile(900);
    const [thumbnail, setThumbnail] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(
        document.createElement("canvas")
    );

    useEffect(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        // common settings
        video.crossOrigin = "anonymous";
        video.muted = true;
        video.playsInline = true;

        if (isMobile) {
            // --- MOBILE: wait for canplay, then draw & cleanup ---
            const handleCanPlay = () => {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    ctx.drawImage(video, 0, 0);
                    setThumbnail(canvas.toDataURL("image/png"));
                }
                video.removeEventListener("canplay", handleCanPlay);
                video.pause();
                video.src = "";
            };

            video.addEventListener("canplay", handleCanPlay);

            // must actually fetch data for a frame on iOS
            video.preload = "auto";
            video.load();

            return () => {
                video.removeEventListener("canplay", handleCanPlay);
            };
        } else {
            // --- DESKTOP: use loadedmetadata + timeupdate to capture frame ---
            const handleLoadedMetadata = () => {
                video.currentTime = 0;
            };

            const handleTimeUpdate = () => {
                const ctx = canvas.getContext("2d");
                if (!ctx) return;
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                setThumbnail(canvas.toDataURL("image/png"));
                video.pause();
            };

            video.addEventListener("loadedmetadata", handleLoadedMetadata);
            video.addEventListener("timeupdate", handleTimeUpdate);

            // only metadata needed on desktop
            video.preload = "metadata";
            video.load();

            return () => {
                video.removeEventListener(
                    "loadedmetadata",
                    handleLoadedMetadata
                );
                video.removeEventListener("timeupdate", handleTimeUpdate);
            };
        }
    }, [videoUrl, isMobile]);

    // off‑screen style for mobile so the element still "renders" in iOS.
    // Must use position:"fixed" (not "absolute") so it's out of document
    // flow. top/left must stay within the viewport (not large negative
    // values) — large negative coordinates on position:fixed can still
    // trigger horizontal overflow/scrollbar on Android Chrome during video
    // upload, which disappears once the element is removed from the DOM.
    const offScreenStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
    };

    return (
        <>
            {thumbnail && thumbnail.length > 10 ? (
                <img src={thumbnail} alt={alt} className={className} />
            ) : (
                 <div
                    className={className}
                    style={{
                        background: "#000",
                    }}
                />
            )}

            <video
                ref={videoRef}
                src={videoUrl || ""}
                // desktop can hide fully, mobile uses tiny off‑screen box
                style={isMobile ? offScreenStyle : { display: "none" }}
            />
        </>
    );
};

export default VideoThumbnail;
